import React, { FC, useState } from "react";
import styled from "styled-components";
import { Card, Form, Input, Button, Tag, Popconfirm } from "antd";
import { Store } from "antd/lib/form/interface";
import { useDispatch, useSelector } from "react-redux";
import user from "../../ducks/user";
import { ContactInfo } from "../../types";
import { FormInstance } from "antd/lib/form";
import { RootState } from "../../ducks";
import { ViewTypeUpdater } from ".";
import { UserOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { useUpdateMyContactInfoMutation } from "../../queries/auto-generated/hooks";
import useLogout from "../../hooks/useLogout";

const Contact: FC<{ setType: ViewTypeUpdater }> = ({ setType }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [updateMyContactInfoMutation] = useUpdateMyContactInfoMutation();
  const { contactInfo, editing, loggedIn, isGuest } = useSelector(
    (state: RootState) => state.user
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const { logout } = useLogout();

  const isPreview = !!contactInfo && !editing;

  const handleButtonClick = () => {
    if (!isPreview) {
      form.submit();
    } else {
      dispatch(user.actions.updatingEditing(isPreview));
      if (isGuest) {
        dispatch(user.actions.stopBeingGuest());
      }
    }
  };

  const handleSubmit = (values: Store) => {
    const { firstName, lastName, email, phoneNumber } = values;
    const newContactInfo: ContactInfo = {
      firstName,
      lastName,
      email,
      phoneNumber,
    };

    // Update with server if logged in
    if (loggedIn) {
      // TODO: Only update with server if the data is different
      const problemMessage = "Unable to update contact information";
      updateMyContactInfoMutation({
        variables: {
          input: {
            firstName,
            lastName,
            phoneNumber,
          },
        },
      })
        .then(({ data }) => {
          console.log({ data });
          if (!data) {
            throw new Error(problemMessage);
          }

          const { firstName, lastName, phoneNumber } = data.updateMyContactInfo;

          dispatch(
            user.actions.updateContactInfo({
              contactInfo: {
                firstName,
                lastName,
                phoneNumber,
                email: email as string,
              },
            })
          );
        })
        .catch((e) => {
          console.error({ e });
          setErrorMessages([problemMessage]);
        });
    } else {
      dispatch(user.actions.becomeGuest());

      dispatch(user.actions.updateContactInfo({ contactInfo: newContactInfo }));
    }
    dispatch(user.actions.updatingEditing(isPreview));
  };

  const ErrorMessages: FC = () => {
    if (!errorMessages || errorMessages.length === 0) {
      return null;
    }

    return (
      <StyledList>
        {errorMessages.map((message) => (
          <li>{message}</li>
        ))}
      </StyledList>
    );
  };

  return (
    <StyledCard
      title="Your contact info"
      extra={
        loggedIn ? (
          <Popconfirm
            placement="topRight"
            title="Would you like to sign out?"
            cancelText="No"
            okText="Yes"
            onConfirm={logout}
          >
            <Tag>Logged in</Tag>
          </Popconfirm>
        ) : (
          ""
        )
      }
      actions={
        isPreview
          ? [
              <Button
                type="text"
                size="large"
                shape="round"
                block
                onClick={handleButtonClick}
              >
                Edit
              </Button>,
            ]
          : undefined
      }
    >
      {isPreview ? (
        <Preview contactInfo={contactInfo as ContactInfo} />
      ) : (
        [
          <ContactInfoForm
            form={form}
            handleSubmit={handleSubmit}
            loggedIn={loggedIn}
          />,
          <ErrorMessages />,
          <Button
            type={isPreview ? "text" : "primary"}
            size="large"
            shape="round"
            block
            onClick={handleButtonClick}
          >
            {isPreview ? "Edit" : "Continue"}
          </Button>,
          loggedIn ? null : (
            <AlternativeOptions>
              <Button
                type="text"
                shape="round"
                onClick={() => setType(undefined)}
              >
                Create account or login
              </Button>
            </AlternativeOptions>
          ),
        ]
      )}
    </StyledCard>
  );
};

const Preview: FC<{ contactInfo: ContactInfo }> = ({ contactInfo }) => {
  const { firstName, lastName, email, phoneNumber } = contactInfo;
  return (
    <div>
      <div>{`${firstName} ${lastName}, ${phoneNumber}`}</div>
      <div>{`${email}`}</div>
    </div>
  );
};

const ContactInfoForm: FC<{
  form: FormInstance;
  handleSubmit: (values: Store) => void;
  loggedIn: boolean;
}> = ({ form, handleSubmit, loggedIn }) => {
  const contactInfo = useSelector((state: RootState) => state.user.contactInfo);

  const initialValues = contactInfo ? contactInfo : undefined;

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="contact_info"
      scrollToFirstError
      colon={false}
      hideRequiredMark
      onFinish={handleSubmit}
    >
      <StyledItem
        name="firstName"
        rules={[
          {
            required: true,
            message: "Please input your first name",
          },
        ]}
      >
        <Input placeholder="First name" prefix={<UserOutlined />} />
      </StyledItem>
      <StyledItem
        name="lastName"
        rules={[
          {
            required: true,
            message: "Please input your last name",
          },
        ]}
      >
        <Input placeholder="Last name" prefix={<UserOutlined />} />
      </StyledItem>
      <StyledItem
        name="email"
        rules={[
          {
            required: true,
            message: "Please input your email address",
            type: "email",
          },
        ]}
      >
        <Input
          placeholder="Email"
          prefix={<MailOutlined />}
          disabled={loggedIn}
          title={loggedIn ? "Cannot change email at this time" : ""}
        />
      </StyledItem>
      <StyledItem
        name="phoneNumber"
        rules={[{ required: true, message: "Please enter your phone number" }]}
      >
        <Input placeholder="Phone number" prefix={<PhoneOutlined />} />
      </StyledItem>
    </Form>
  );
};

const StyledCard = styled(Card)``;

const StyledItem = styled(Form.Item)`
  display: block;
`;

const AlternativeOptions = styled.div`
  padding-top: 2em;
  text-align: center;
  display: block;
`;

const StyledList = styled.ul`
  list-style: none;
  color: #a61d24;
  padding-left: 0;
`;

export default Contact;
